import {
  connectMainSocket,
  getMainSocket,
  onSocketAuthenticated,
  onSocketConnect,
  queueEmitUntilConnected,
  waitForMainSocketAuthenticated
} from '../../clients/mainSocketClient'
import { MAIN_SOCKET_API_EVENTS } from '../../constants/api/mainSocket'
import { emitWithCallbackAck } from './socketCallbackAck'

const realtimeHandlers = {
  onNewMessage: null,
  onMessageStatus: null,
  onChatGroupDeleted: null,
  onChatGroupUpdated: null,
  onChatGroupCreated: null
}

const detachRealtimeListeners = socket => {
  if (!socket) return
  socket.off(MAIN_SOCKET_API_EVENTS.MESSAGE_CREATED)
  socket.off(MAIN_SOCKET_API_EVENTS.MESSAGES_CREATED)
  socket.off(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGE_CREATED)
  socket.off(MAIN_SOCKET_API_EVENTS.MESSAGES_RECEIVED)
  socket.off(MAIN_SOCKET_API_EVENTS.MESSAGES_READ)
  socket.off(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_READ)
  socket.off(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_DELETED)
  socket.off(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_UPDATED)
  socket.off(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_CREATED)
}

const attachRealtimeListeners = () => {
  const socket = getMainSocket()
  if (!socket) return
  detachRealtimeListeners(socket)
  if (realtimeHandlers.onNewMessage) {
    const forward = payload => realtimeHandlers.onNewMessage(payload)
    socket.on(MAIN_SOCKET_API_EVENTS.MESSAGE_CREATED, forward)
    socket.on(MAIN_SOCKET_API_EVENTS.MESSAGES_CREATED, forward)
    socket.on(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGE_CREATED, forward)
  }
  if (realtimeHandlers.onMessageStatus) {
    const forward = payload => realtimeHandlers.onMessageStatus(payload)
    socket.on(MAIN_SOCKET_API_EVENTS.MESSAGES_RECEIVED, forward)
    socket.on(MAIN_SOCKET_API_EVENTS.MESSAGES_READ, forward)
    socket.on(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_READ, forward)
  }
  if (realtimeHandlers.onChatGroupDeleted) {
    socket.on(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_DELETED, payload =>
      realtimeHandlers.onChatGroupDeleted(payload)
    )
  }
  if (realtimeHandlers.onChatGroupUpdated) {
    socket.on(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_UPDATED, payload =>
      realtimeHandlers.onChatGroupUpdated(payload)
    )
  }
  if (realtimeHandlers.onChatGroupCreated) {
    socket.on(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_CREATED, payload =>
      realtimeHandlers.onChatGroupCreated(payload)
    )
  }
}

onSocketConnect(attachRealtimeListeners)
onSocketAuthenticated(attachRealtimeListeners)

const EMIT_WITH_ACK_TIMEOUT_MS = 30000

// Emit with a one-shot response event (message:list, etc.)
const emitWithAck = async (event, payload, resultEvent) => {
  const socket = await connectMainSocket()

  if (!socket) {
    return Promise.reject(new Error('Socket not connected'))
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      socket.off(resultEvent, handleResult)
      reject(new Error(`Socket response timeout for ${event}`))
    }, EMIT_WITH_ACK_TIMEOUT_MS)

    const handleResult = data => {
      clearTimeout(timer)
      socket.off(resultEvent, handleResult)
      resolve(data)
    }

    socket.on(resultEvent, handleResult)
    queueEmitUntilConnected(event, payload)
  })
}

/** Legacy/mobile: server acks via Socket.IO callback, then broadcasts `messages created`. */
const Create = async messageData => emitWithCallbackAck(
  MAIN_SOCKET_API_EVENTS.CREATE_MESSAGE,
  messageData
)

const CreateForChatGroup = async messageData => emitWithCallbackAck(
  MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_CREATE,
  messageData
)

const FindMany = async listMetaData => emitWithAck(
  MAIN_SOCKET_API_EVENTS.LIST_MESSAGES,
  listMetaData.toAxiosRequestParams(),
  MAIN_SOCKET_API_EVENTS.LIST_MESSAGES_RESULT
)

/**
 * Fetch messages for a chat group via chatGroupMessages::get (response on chatGroupMessages::get::response).
 * Response format is the same as message:list::response ({ totalCount, items } or { data: { totalCount, items } }).
 */
const FindManyForChatGroup = async (chatGroupId, deviceUuid, listMetaData) => {
  const payload = {
    chatGroupId,
    deviceUuid: deviceUuid ?? null,
    ...listMetaData.toAxiosRequestParams()
  }
  return emitWithAck(
    MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_GET,
    payload,
    MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_GET_RESULT
  )
}

const onNewMessage = callback => {
  realtimeHandlers.onNewMessage = callback
  connectMainSocket()
    .then(() => attachRealtimeListeners())
    .catch(() => { /* no token / offline; listeners attach after login */ })
}

/**
 * Notify the server that the operator has read the messages of a device thread.
 * Mirrors the legacy `messages::read` contract: when no explicit message ids are
 * sent, the server marks all unread relations for `uuid_device_target` as read and
 * broadcasts `messages read`. `read` must be the string `'true'` (server checks `=== 'true'`).
 * @param {string} uuidDeviceTarget - device uuid of the opened thread
 */
const markThreadRead = async uuidDeviceTarget => {
  if (!uuidDeviceTarget) return
  const socket = await connectMainSocket()
  if (!socket) return
  queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.MARK_MESSAGES_READ, {
    uuid_device_target: uuidDeviceTarget,
    read: 'true'
  })
}

/**
 * Notify the server that the operator received a device-originated message.
 * Mirrors legacy web auto-ack on `messages created` so the device sees delivery (✓✓).
 * @param {string} messageId - persisted message id (`id_message`)
 * @param {string} uuidDeviceTarget - device uuid from the socket payload (`uuid_device_target`)
 */
const markMessageReceived = async (messageId, uuidDeviceTarget) => {
  if (!messageId || !uuidDeviceTarget) return
  const socket = await connectMainSocket()
  if (!socket) return
  queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.MARK_MESSAGES_RECEIVED, {
    received: 'true',
    uuid_device_target: uuidDeviceTarget,
    messages: [messageId]
  })
}

/** Mark all unread messages in a chat group as read for the logged-in user. */
const markChatGroupRead = async chatGroupId => {
  if (!chatGroupId) return
  const socket = await connectMainSocket()
  if (!socket) return
  queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.MARK_CHAT_GROUP_MESSAGES_READ, {
    read: 'true',
    chatGroupId
  })
}

/**
 * Subscribe to delivery (`messages received`) and read (`messages read`) receipts.
 * The callback receives the raw server payload, which carries either `received: true`
 * or `read: true` plus `messages: [{ id_message, uuid_device }]`.
 */
const onMessageStatus = callback => {
  realtimeHandlers.onMessageStatus = callback
  connectMainSocket()
    .then(() => attachRealtimeListeners())
    .catch(() => { /* no token; attach after login */ })
}

const onChatGroupDeleted = callback => {
  realtimeHandlers.onChatGroupDeleted = callback
  connectMainSocket()
    .then(() => attachRealtimeListeners())
    .catch(() => { /* no token; attach after login */ })
}

const onChatGroupUpdated = callback => {
  realtimeHandlers.onChatGroupUpdated = callback
  connectMainSocket()
    .then(() => attachRealtimeListeners())
    .catch(() => { /* no token; attach after login */ })
}

const onChatGroupCreated = callback => {
  realtimeHandlers.onChatGroupCreated = callback
  connectMainSocket()
    .then(() => attachRealtimeListeners())
    .catch(() => { /* no token; attach after login */ })
}

/** Connect socket and bind push listeners (idempotent). Call after login. Failures must not affect REST. */
const ensureRealtimeChat = async () => {
  try {
    const socket = await connectMainSocket()
    if (socket) {
      await waitForMainSocketAuthenticated()
    }
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[MessageSocket] Real-time setup failed; REST CRUD is unaffected.', e?.message || e)
    }
  }
  attachRealtimeListeners()
}

/** Latest open thread; re-subscribed once on each socket reconnect (no stacked handlers). */
let activeChatContext = null

const resubscribeActiveChatContext = () => {
  if (!activeChatContext) return
  const { chatGroupId, userId, deviceId } = activeChatContext
  queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.JOIN, { chatGroupId, userId, deviceId })
  if (chatGroupId) {
    queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_SUBSCRIBE, { chatGroupId })
  }
}

onSocketAuthenticated(resubscribeActiveChatContext)

const joinChatContext = async ({ chatGroupId, userId, deviceId }) => {
  const socket = await connectMainSocket()
  if (!socket) return
  activeChatContext = { chatGroupId, userId, deviceId }
  resubscribeActiveChatContext()
}

const leaveChatContext = async ({ chatGroupId, userId, deviceId }) => {
  const socket = await connectMainSocket()
  if (!socket) return
  const payload = { chatGroupId, userId, deviceId }
  socket.emit(MAIN_SOCKET_API_EVENTS.LEAVE, payload)
  if (chatGroupId) {
    socket.emit(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_UNSUBSCRIBE, { chatGroupId })
  }
  if (
    activeChatContext &&
    activeChatContext.chatGroupId === chatGroupId &&
    activeChatContext.userId === userId &&
    activeChatContext.deviceId === deviceId
  ) {
    activeChatContext = null
  }
}

const subscribeAllChatGroupsForNotifications = async chatGroupIds => {
  const socket = await connectMainSocket()
  if (!socket || !Array.isArray(chatGroupIds)) return
  const uniqueIds = [...new Set(chatGroupIds.map(id => String(id).trim()).filter(Boolean))]
  for (const chatGroupId of uniqueIds) {
    queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.CHAT_GROUP_MESSAGES_SUBSCRIBE, { chatGroupId })
  }
}

const MessageSocket = {
  Create,
  CreateForChatGroup,
  FindMany,
  FindManyForChatGroup,
  onNewMessage,
  onMessageStatus,
  onChatGroupDeleted,
  onChatGroupUpdated,
  onChatGroupCreated,
  ensureRealtimeChat,
  markThreadRead,
  markMessageReceived,
  markChatGroupRead,
  joinChatContext,
  leaveChatContext,
  subscribeAllChatGroupsForNotifications
}

export default MessageSocket
